import React, { useState } from 'react';
import {
  Shield, Search, Filter, Download, RefreshCw,
  CheckCircle, AlertCircle, LogIn
} from 'lucide-react';
import Pagination from '../common/Pagination';
import { useAuditStats, useAuditLogs } from '../../hooks/useAudit';
import AuditDetail from './components/AuditDetail';

// ── Helpers ──────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleString('es-DO', {
  month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
}) : '—';

const ACTION_COLOR = {
  CREATE:  'bg-green-100 text-green-700',
  UPDATE:  'bg-blue-100 text-blue-700',
  DELETE:  'bg-red-100 text-red-700',
  LOGIN:   'bg-purple-100 text-purple-700',
  LOGOUT:  'bg-gray-100 text-gray-600',
  APPROVE: 'bg-emerald-100 text-emerald-700',
  REJECT:  'bg-orange-100 text-orange-700',
  PROCESS: 'bg-indigo-100 text-indigo-700',
  EXPORT:  'bg-yellow-100 text-yellow-700',
  ACCESS:  'bg-slate-100 text-slate-600',
};

const MODULES = [
  'auth','empleados','nomina','vacaciones','prestaciones',
  'prestamos','bonificaciones','regalia','acciones-personal',
  'deducciones','provisiones','reportes','configuracion',
];
const ACTIONS = [
  'CREATE','UPDATE','DELETE','LOGIN','LOGOUT',
  'APPROVE','REJECT','PROCESS','EXPORT','ACCESS',
];

// ── Stat Card ─────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value ?? '—'}</p>
      </div>
    </div>
  );
}

// ── Module ────────────────────────────────────────────────────
export default function AuditModule() {
  const [filters, setFilters] = useState({});
  const [page, setPage]       = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const { stats } = useAuditStats();
  const { data, pagination, loading, reload } = useAuditLogs(filters, page);

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val || undefined }));
    setPage(1);
  };

  const clearFilters = () => { setFilters({}); setSearchInput(''); setPage(1); };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilter('search', searchInput);
  };

  const exportCSV = () => {
    if (!data.length) return;
    const headers = ['ID','Usuario','Rol','Módulo','Entidad','ID Registro','Acción','Descripción','Estado','Fecha'];
    const rows = data.map(r => [
      r.id, r.user_name, r.user_role, r.module, r.entity,
      r.entity_id, r.action, r.description, r.status,
      new Date(r.created_at).toISOString()
    ]);
    const csv = '\ufeff' + [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-slate-100 p-2 rounded-lg">
            <Shield className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Auditoría del Sistema</h1>
            <p className="text-xs text-gray-400">Registro inmutable de todas las acciones</p>
          </div>
        </div>
        <button onClick={reload} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total eventos"    value={stats?.total?.toLocaleString()}    icon={Shield}       color="bg-slate-100 text-slate-600" />
        <StatCard label="Hoy"              value={stats?.hoy?.toLocaleString()}      icon={CheckCircle}  color="bg-green-100 text-green-600" />
        <StatCard label="Errores"          value={stats?.errores?.toLocaleString()}  icon={AlertCircle}  color="bg-red-100 text-red-600" />
        <StatCard label="Logins"           value={stats?.logins?.toLocaleString()}   icon={LogIn}        color="bg-purple-100 text-purple-600" />
      </div>

      {/* Filters + Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex gap-2">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Buscar por usuario, descripción o ID de registro..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 outline-none"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              Buscar
            </button>
          </form>
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              showFilters || activeFiltersCount > 0
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="bg-blue-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Módulo</label>
              <select value={filters.module || ''} onChange={e => setFilter('module', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none">
                <option value="">Todos</option>
                {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Acción</label>
              <select value={filters.action || ''} onChange={e => setFilter('action', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none">
                <option value="">Todas</option>
                {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Estado</label>
              <select value={filters.status || ''} onChange={e => setFilter('status', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none">
                <option value="">Todos</option>
                <option value="success">Éxito</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Desde</label>
              <input type="date" value={filters.dateFrom || ''} onChange={e => setFilter('dateFrom', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Hasta</label>
              <input type="date" value={filters.dateTo || ''} onChange={e => setFilter('dateTo', e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none" />
            </div>
            <div className="flex items-end">
              <button onClick={clearFilters} className="w-full px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Eventos {pagination ? `(${pagination.total.toLocaleString()})` : ''}
          </p>
          {pagination && (
            <p className="text-xs text-gray-400">
              Página {pagination.page} de {pagination.totalPages}
            </p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-left">Módulo</th>
                <th className="px-4 py-3 text-left">Acción</th>
                <th className="px-4 py-3 text-left">Descripción</th>
                <th className="px-4 py-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600 mx-auto" />
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                  No hay eventos para los filtros seleccionados
                </td></tr>
              ) : data.map(log => (
                <tr
                  key={log.id}
                  onClick={() => setSelectedId(log.id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-800">{log.user_name || '—'}</p>
                    <p className="text-[10px] text-gray-400">{log.user_role || ''}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{log.module}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ACTION_COLOR[log.action] || 'bg-gray-100 text-gray-600'}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 max-w-xs truncate">{log.description || '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    {log.status === 'success'
                      ? <CheckCircle className="w-3.5 h-3.5 text-green-500 mx-auto" />
                      : <AlertCircle className="w-3.5 h-3.5 text-red-500 mx-auto" />
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <Pagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            pageSize={50}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <AuditDetail logId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
